import {actionTool, navigationTool, noInput, objectInput, readOnlyTool, stringInput, waitForSecrets} from "@/functions/webMcpTool";
import {describeCurrentStep} from "@/functions/webMcpStep";
import {chooseOption, cleanText, fillInput, formatPhoneForInput, isVisible, press, pressButtonWithText, setChecked, waitForElement, waitForOutcome} from "@/functions/webMcpDom";
import getBrandName from "@/functions/getBrandName";

const MAX_ACTIVITY_ROWS = 20
const POLL_MS = 100
const EMERGENCY_CODES_NOTE = 'New emergency codes are showing on the page. Ask the person to download or write them down now; they are not shared with you, and the old codes no longer work.'

const EXIT_BUTTONS = {
	AccountInfo: ['Done'],
	AccountActivity: ['Done'],
	EditUsername: ['Cancel'],
	EditPassword: ['Cancel'],
	EditEmail: ['Cancel'],
	EditSecondEmail: ['Cancel'],
	ConfirmDeleteSecondEmail: ['No, Cancel'],
	EditMobilePhone: ['Cancel'],
	ConfirmDeleteMobilePhone: ['No, Cancel'],
	VerifySMS: ['Skip'],
	EditSecuritySettingsPassword: ['Previous Page'],
	EditSecuritySettings: ['Done', 'Previous Page'],
	EnableAuthApp: ['Previous Page'],
	EnableAuthAppQrCode: ['Previous Page'],
	EnableEmergencyCodes: ['Previous Page'],
	EnableTextVerification: ['Previous Page'],
	Passkey: ['Done'],
}

const METHODS = {
	signIn: {both: '#bothOption', phone: '#phoneOption', email: '#emailOption'},
	signInValues: {BOTH: 'both', PHONE: 'phone', EMAIL: 'email'},
	phone: {text: '#smsOption', call: '#voiceOption'},
	phoneValues: {SMS: 'text', VOICE: 'call'},
}

const booleanInput = (description) => ({type: 'boolean', description})

const digitsOf = (value) => String(value ?? '').replace(/\D/g, '')

const mainArea = () => document.getElementById('mainContentArea') ?? document.body

const waitUntil = (isDone, timeoutMs = 5000) => new Promise((resolve) => {
	const startedAt = Date.now()
	const timer = setInterval(() => {
		if (isDone() || Date.now() - startedAt > timeoutMs) {
			clearInterval(timer)
			resolve()
		}
	}, POLL_MS)
})

const isPageLoading = () => Boolean(mainArea().querySelector('img[alt^="Loading account"]'))

const withNote = async (note) => ({result: {...(await waitForOutcome({describeStep: describeCurrentStep})), note}})

const submit = (formId, description) => press(`button[type="submit"][form="${formId}"]`, description)

const readOverviewRow = (overview, row) => {
	const [labelColumn, valueColumn] = row.children
	const label = cleanText(labelColumn?.querySelector('span')).replace(/:$/, '')
	if (!valueColumn || !label) return
	const verifiedIcon = valueColumn.querySelector('img[alt$="verified"]')
	const saysNo = [...valueColumn.querySelectorAll('span')].some((span) => /^No\b/.test(cleanText(span)))
	const verified = verifiedIcon ? true : saysNo ? false : undefined
	const value = cleanText(valueColumn.querySelector('span.mb-2') ?? valueColumn.querySelector('span'))
	if (label === 'Username') {
		overview.username = value
	} else if (label === 'Passkeys') {
		overview.passkeys = [...valueColumn.querySelectorAll('p span')].map(cleanText).filter(Boolean)
	} else if (label.startsWith('2-Step Verification')) {
		overview.twoStepVerification = [...valueColumn.querySelectorAll('p')].map((line) => ({
			method: cleanText(line).replace(cleanText(line.querySelector('span.fw-bold')), '').replace('(Default)', '').trim(),
			status: cleanText(line.querySelector('span.fw-bold')),
			isDefault: cleanText(line).includes('(Default)') || undefined,
		}))
	} else if (label.includes('Email') && value) {
		overview.emails.push({label, email: value, verified})
	} else if (label.includes('Phone Number') && value) {
		overview.phones.push({label, phone: value, verified})
	}
}

const readOverview = () => {
	const overview = {username: undefined, passkeys: [], twoStepVerification: [], emails: [], phones: []}
	mainArea().querySelectorAll('.row.py-3').forEach((row) => readOverviewRow(overview, row))
	return overview
}

const phoneRows = () => [...mainArea().querySelectorAll('.row.py-3')]
	.filter((row) => cleanText(row.children[0]).includes('Phone Number') && row.querySelector('span.mb-2'))

const readActivity = () => [...mainArea().querySelectorAll('.d-flex.px-2.py-2')].slice(0, MAX_ACTIVITY_ROWS).map((record) => {
	const [label, time] = record.querySelectorAll('.justify-content-between > span')
	return {
		date: cleanText(record.closest('.mb-32')?.querySelector('p.fw-bold')) || undefined,
		activity: cleanText(label),
		time: cleanText(time) || undefined,
		detail: cleanText(record.querySelector('.help-text')) || undefined,
	}
})

const readSecuritySettings = () => Object.fromEntries([...mainArea().querySelectorAll('.info-tile')].map((tile) => [
	cleanText(tile.querySelector('span.gray')).replace(/:$/, ''),
	cleanText(tile.firstElementChild?.children[1]),
]))

const SECURITY_SETTINGS = {phone_email: 'Phone/Email', authenticator_app: 'Authenticator App', emergency_codes: 'Emergency Codes'}

const checkedValue = (name) => document.querySelector(`input[name="${name}"]:checked`)?.value

const readPasskeys = () => [...mainArea().querySelectorAll('.passkey-tile')].map((tile) => {
	const columns = tile.children
	return {
		name: cleanText(columns[0]?.querySelector('.tp')),
		created: cleanText(columns[2]?.querySelector('.tp')) || undefined,
		tile,
	}
})

const buildAccountOverview = () => readOnlyTool({
	name: 'get_account_overview',
	description: `Read the person's ${getBrandName()} account profile as it shows on this page: username, emails and phone numbers with whether each is verified, passkey names, and which 2-step verification methods are on. Values are shown the way the page displays them, and the password is never included.`,
	execute: async () => {
		await waitUntil(() => !isPageLoading())
		return readOverview()
	},
})

const buildSendEmailVerification = () => actionTool({
	name: 'send_email_verification',
	description: 'Email the person a link that verifies their main email address, as the "Verify Email" button on the account page does. Only available while the email shows as not verified in get_account_overview. The person opens the email and clicks the link themselves; nothing else on the account changes.',
	run: () => {
		pressButtonWithText('Verify Email')
		return withNote('If no message appeared, a verification email was sent. The person opens it and follows the link.')
	},
})

const buildSendPhoneVerification = () => actionTool({
	name: 'send_phone_verification',
	description: 'Send a verification code to one of the person\'s unverified phone numbers by text or phone call, as the "Verify with Text" and "Verify with Call" buttons do. The site then asks for the code. Use a phone number exactly as get_account_overview shows it.',
	inputSchema: objectInput({
		phone: stringInput('One of the unverified phone numbers get_account_overview lists.', {maxLength: 20}),
		method: stringInput('How the code is delivered.', {enum: ['text', 'call']}),
	}, ['phone', 'method']),
	run: ({phone, method}) => {
		const row = phoneRows().find((candidate) => digitsOf(cleanText(candidate.querySelector('span.mb-2'))) === digitsOf(phone))
		if (!row) throw new Error('That phone number is not on this account. Call get_account_overview for the numbers shown.')
		const buttonText = method === 'call' ? 'Verify with Call' : 'Verify with Text'
		const button = [...row.querySelectorAll('button')].find((candidate) => cleanText(candidate) === buttonText)
		if (!button) throw new Error('That phone number is already verified.')
		button.click()
	},
})

const buildAccountActivity = () => readOnlyTool({
	name: 'get_account_activity',
	description: `Read the recent sign-in and account activity on this page, newest first, up to ${MAX_ACTIVITY_ROWS} entries, with the date, time, and detail each one shows. Use it when the person wants to check for sign-ins they don't recognize. Use show_more_activity for older entries, and remove_trusted_devices if something looks wrong.`,
	untrusted: true,
	execute: async () => {
		await waitUntil(() => !isPageLoading())
		return {activity: readActivity()}
	},
})

const buildShowMoreActivity = () => readOnlyTool({
	name: 'show_more_activity',
	description: 'Load older account activity on this page, as the "Show more" button does, then return the activity list, still capped at the newest entries this tool reads. Use it after get_account_activity when the person is looking further back. Returns an error when there is no older activity to show.',
	untrusted: true,
	execute: async () => {
		pressButtonWithText('Show more')
		await waitForOutcome()
		return {activity: readActivity()}
	},
})

const buildRemoveTrustedDevices = () => actionTool({
	name: 'remove_trusted_devices',
	description: 'Remove every trusted device from the account, as "Remove All Trusted Devices" does. After this, the site asks for a verification code the next time the person signs in on any device, including this one. It takes effect at once with no confirmation from the site, so confirm with the person first.',
	run: () => pressButtonWithText('Remove All Trusted Devices'),
})

const buildChangeUsername = () => actionTool({
	name: 'change_username',
	description: `Change the person's ${getBrandName()} username on the edit-username page. The current password goes into the page from the person or their password manager, never through this tool, so it waits until that field is filled. The site shows a message if the password is wrong or the new username is taken or not allowed; usernames can only be changed so often. Returns the next page, or that message.`,
	inputSchema: objectInput({
		newUsername: stringInput('The new username the person chose.', {maxLength: 100}),
	}, ['newUsername']),
	run: ({newUsername}) => {
		fillInput('#username', newUsername, 'new username field')
		return waitForSecrets('change_username', ['#password']) ?? submit('editUsernameForm', 'Save and Continue button')
	},
})

const buildChangePassword = () => actionTool({
	name: 'change_password',
	description: `Save a new ${getBrandName()} password on the edit-password page. The current and new passwords go into the page from the person or their password manager, never through this tool, so it waits until all three fields are filled. The site rejects a new password that is too weak or has appeared in a data breach, and says why. Returns the next page, or the page's message.`,
	inputSchema: noInput,
	run: () => waitForSecrets('change_password', ['#password', '#newPassword', '#confirmNewPassword']) ?? submit('editPasswordForm', 'Save and Continue button'),
})

const buildChangeEmail = () => actionTool({
	name: 'change_email',
	description: `Change the main email address on the person's ${getBrandName()} account on the edit-email page. The current password goes into the page from the person or their password manager, never through this tool, so it waits until that field is filled. The site may then ask the person to verify the new address from their inbox. Returns the next page on success, or the page's message about what to fix.`,
	inputSchema: objectInput({
		newEmail: stringInput('The new email address. It is entered in both email fields.', {maxLength: 100}),
	}, ['newEmail']),
	run: ({newEmail}) => {
		fillInput('#newEmail', newEmail, 'new email field')
		fillInput('#confirmNewEmail', newEmail, 'confirm new email field')
		return waitForSecrets('change_email', ['#password']) ?? submit('editEmailForm', 'Save and Continue button')
	},
})

const buildChangeSecondEmail = () => actionTool({
	name: 'change_second_email',
	description: 'Add or change the second email address on the account, which the site uses as a backup for sign-in codes and account recovery. The current password goes into the page from the person or their password manager, never through this tool, so it waits until that field is filled. Returns the next page on success, or the page\'s message if the password is wrong or the address is not valid.',
	inputSchema: objectInput({
		email: stringInput('The second email address. It is entered in both email fields.', {maxLength: 100}),
	}, ['email']),
	run: ({email}) => {
		fillInput('#email', email, 'second email field')
		fillInput('#confirmNewEmail', email, 'confirm second email field')
		return waitForSecrets('change_second_email', ['#password']) ?? submit('editEmailForm', 'Save and Continue button')
	},
})

const buildRemoveSecondEmail = (formId) => () => actionTool({
	name: 'remove_second_email',
	description: 'Remove the second email address from the account, leaving only the main email for sign-in codes and recovery. The current password goes into the page from the person or their password manager, never through this tool, so it waits until that field is filled. Confirm with the person before removing it. Returns the next page on success, or the page\'s message if the password is wrong.',
	inputSchema: noInput,
	run: () => {
		if (formId === 'editEmailForm') {
			fillInput('#email', '', 'second email field')
			fillInput('#confirmNewEmail', '', 'confirm second email field')
		}
		return waitForSecrets('remove_second_email', ['#password']) ?? submit(formId, formId === 'editEmailForm' ? 'Save and Continue button' : 'Delete Email button')
	},
})

const buildChangePhone = () => actionTool({
	name: 'change_phone',
	description: 'Add or change the phone number this page is editing. The current password goes into the page from the person or their password manager, never through this tool, so it waits until that field is filled. The site then usually sends a code by text or call to verify the new number and asks for it on the next page, where verify_phone continues. Returns that next page, or the page\'s message about what to fix.',
	inputSchema: objectInput({
		phone: stringInput('The new 10-digit US phone number, for example 801-555-1234.', {maxLength: 20}),
		verifyBy: stringInput('How to send the verification code. Defaults to text.', {enum: ['text', 'call']}),
	}, ['phone']),
	run: ({phone, verifyBy = 'text'}) => {
		fillInput('#phone', formatPhoneForInput(phone), 'phone number field')
		chooseOption(METHODS.phone[verifyBy] ?? METHODS.phone.text, 'verification method choice')
		return waitForSecrets('change_phone', ['#password']) ?? submit('editPhoneForm', 'Save and Continue button')
	},
})

const buildRemovePhone = () => actionTool({
	name: 'remove_phone',
	description: 'Remove the phone number shown on this page from the account, so it no longer receives sign-in or recovery codes. The current password goes into the page from the person or their password manager, never through this tool, so it waits until that field is filled. Confirm with the person before removing it. Returns the next page on success, or the page\'s message if the password is wrong.',
	inputSchema: noInput,
	run: () => waitForSecrets('remove_phone', ['#password']) ?? submit('deletePhoneForm', 'Delete Phone Number button'),
})

const buildVerifyPhone = () => actionTool({
	name: 'verify_phone',
	description: 'Continue once the verification code the person received by text or call for their new or unverified phone number is in the page. The person types it, or the browser fills it from the text message; it never passes through this tool, so it waits until the field is filled. Returns the next page once the number is verified, or the site\'s message if the code is wrong or expired.',
	inputSchema: noInput,
	run: () => waitForSecrets('verify_phone', ['#verify-code']) ?? submit('verifyPhoneForm', 'Continue button'),
})

const buildResendPhoneCode = () => actionTool({
	name: 'resend_phone_code',
	description: 'Send a new phone verification code to the same number, as "Send New Code" does on the verify-phone page. Use it when the person did not get the first code or it expired. The site may refuse if codes are requested too often, and shows a message saying so.',
	run: () => pressButtonWithText('Send New Code'),
})

const buildSkipPhoneVerification = () => navigationTool({
	name: 'skip_phone_verification',
	description: 'Skip verifying the phone number for now, as the "Skip" button does on the verify-phone page. The number stays on the account as unverified, so it is not used for sign-in codes, and can be verified later from the account page. Returns the page the person goes back to.',
	execute: () => pressButtonWithText('Skip'),
})

const buildUnlockSecuritySettings = () => actionTool({
	name: 'unlock_security_settings',
	description: 'Open the person\'s 2-step verification settings, which this page guards with their current password. The current password goes into the page from the person or their password manager, never through this tool, so it waits until that field is filled. Use it before changing phone and email codes, the authenticator app, or emergency codes. Returns the security settings page, or a message if the password is wrong.',
	inputSchema: noInput,
	run: () => waitForSecrets('unlock_security_settings', ['#password']) ?? submit('verifyPasswordForm', 'Continue button'),
})

const buildSecuritySettings = () => readOnlyTool({
	name: 'get_security_settings',
	description: 'Read the 2-step verification methods on the security settings page and whether each is ON, OFF, or the DEFAULT: phone or email codes, the authenticator app, and emergency codes. Use it before open_security_setting to see what is already set up. Nothing on the account changes.',
	execute: () => ({settings: readSecuritySettings()}),
})

const buildOpenSecuritySetting = () => navigationTool({
	name: 'open_security_setting',
	description: 'Open the page that adds or edits one 2-step verification method, as that row\'s Add or Edit button does: phone and email codes, the authenticator app, or emergency codes. Use it from the security settings page. Returns the opened page\'s step, whose tools make the actual change.',
	inputSchema: objectInput({setting: stringInput('Which method to open.', {enum: Object.keys(SECURITY_SETTINGS)})}, ['setting']),
	execute: ({setting}) => {
		const tile = [...mainArea().querySelectorAll('.info-tile')].find((candidate) => cleanText(candidate.querySelector('span.gray')).startsWith(SECURITY_SETTINGS[setting]))
		const button = tile?.querySelector('button.add, button.edit')
		if (!button) throw new Error(`There is no ${SECURITY_SETTINGS[setting]} setting on this page.`)
		button.click()
	},
})

const buildSetAuthenticatorApp = () => actionTool({
	name: 'set_authenticator_app',
	description: 'Turn authenticator app verification on or off. Turning it on for the first time needs the person\'s acknowledgment that they will save the emergency codes shown later; ask them before passing agree. Turning it on leads to a QR code the person scans with their own app.',
	inputSchema: objectInput({
		enabled: booleanInput('True to use an authenticator app, false to stop using one.'),
		agree: booleanInput('The person agreed to save their emergency codes. Needed when turning the app on for the first time.'),
	}, ['enabled']),
	run: ({enabled, agree}) => {
		setChecked(enabled ? '#authYes' : '#authNo', true, 'authenticator app choice')
		const agreement = document.querySelector('#agree')
		if (enabled && agreement && agree !== undefined) setChecked('#agree', agree, 'agreement checkbox')
		return submit('updateAuthAppStatusForm', 'Save and Continue button')
	},
})

const buildVerifyAuthenticatorApp = () => actionTool({
	name: 'verify_authenticator_app',
	description: 'Finish connecting an authenticator app once the person has scanned the QR code on this page and typed the 6-digit code their app shows. The QR code, setup key and code are theirs alone and never pass through this tool, so it waits until the field is filled. Returns the next page, with the emergency codes, or a message if the code is wrong.',
	inputSchema: noInput,
	run: () => waitForSecrets('verify_authenticator_app', ['#verify-code']) ?? submit('verifyAuthAppCode', 'Verify button'),
})

const buildSetAuthenticatorAppOnly = () => actionTool({
	name: 'set_authenticator_app_only',
	description: 'Finish authenticator app setup, as "Done" does, choosing whether the authenticator app and emergency codes become the ONLY way to verify sign in. With only set, email and phone codes are no longer offered; confirm the choice with the person. Returns the security settings page.',
	inputSchema: objectInput({only: booleanInput('True to allow only the authenticator app and emergency codes at sign in.')}, ['only']),
	run: ({only}) => {
		setChecked('#authAppOnly', only, 'authenticator app only checkbox')
		pressButtonWithText('Done')
	},
})

const buildRegenerateEmergencyCodes = () => actionTool({
	name: 'regenerate_emergency_codes',
	description: 'Replace the person\'s emergency codes with a new set, as "New codes" does. The old codes stop working at once, so confirm with the person first. The new codes show only on the page for the person to download or write down; they are never returned to you.',
	run: () => {
		pressButtonWithText('New codes')
		return withNote(EMERGENCY_CODES_NOTE)
	},
})

const buildDownloadEmergencyCodes = () => actionTool({
	name: 'download_emergency_codes',
	description: 'Download the emergency codes on this page as a PDF to the person\'s device, as "Download" does. Use it right after the codes are created or regenerated so the person has a copy they can find later. The file stays with the person; its contents are not returned to you.',
	run: () => {
		pressButtonWithText('Download')
		return {result: {outcome: 'downloaded', note: 'The browser saved a PDF of the emergency codes for the person.'}}
	},
})

const buildKeepEmergencyCodes = () => actionTool({
	name: 'keep_emergency_codes',
	description: 'After the authenticator app is turned off, choose whether to keep emergency code verification. Most people choose not to keep it; if they keep it, they need their saved codes to use it. Returns the emergency codes page when kept, or a confirmation page when turned off.',
	inputSchema: objectInput({keep: booleanInput('True to keep emergency code verification on.')}, ['keep']),
	run: ({keep}) => {
		chooseOption(keep ? '#keepEmergencyCodesYes' : '#keepEmergencyCodesNo', 'keep emergency codes choice')
		return submit('keepEmergencyCodesForm', 'Save and Continue button')
	},
})

const buildSetEmergencyCodes = () => actionTool({
	name: 'set_emergency_codes',
	description: 'Turn emergency code verification on or off. Turning it on needs the person\'s acknowledgment that they will save the codes; ask them before passing agree. While the authenticator app is on, emergency codes stay on and only the Save step applies. Returns the codes page or a confirmation page.',
	inputSchema: objectInput({
		enabled: booleanInput('True to use emergency codes, false to turn them off.'),
		agree: booleanInput('The person agreed to save their emergency codes. Needed when turning them on.'),
	}, ['enabled']),
	run: ({enabled, agree}) => {
		if (document.querySelector('#emergencyCodesYes')) {
			chooseOption(enabled ? '#emergencyCodesYes' : '#emergencyCodesNo', 'emergency codes choice')
		}
		if (enabled && agree !== undefined && document.querySelector('#agree')) setChecked('#agree', agree, 'agreement checkbox')
		return submit('updateEmergencyCodeStatus', 'Save and Continue button')
	},
})

const buildFinish = () => navigationTool({
	name: 'finish',
	description: 'Leave this emergency codes page with its "Done" button and return to the 2-step verification settings. Make sure the person has downloaded or written down their codes first; they are not shown again unless new ones are made. Returns the security settings step.',
	execute: () => pressButtonWithText('Done'),
})

const buildSignInVerificationSettings = () => readOnlyTool({
	name: 'get_sign_in_verification_settings',
	description: 'Read how the site sends sign-in verification codes: which method is chosen (let the person choose, phone only, or email only) and whether phone codes come by text or call. Both choices appear only when the account has a phone number. Nothing on the account changes.',
	execute: () => ({
		signInMethod: METHODS.signInValues[checkedValue('defaultVerificationMethod')] ?? null,
		phoneMethod: METHODS.phoneValues[checkedValue('defaultPhoneMethod')] ?? null,
		hasPhoneChoices: Boolean(document.querySelector('input[name="defaultVerificationMethod"]')),
	}),
})

const buildSetSignInVerification = () => actionTool({
	name: 'set_sign_in_verification',
	description: 'Choose how sign-in verification codes are sent, then save with "Done". Pass the sign-in method, the phone method, or both; these choices exist only when the account has a phone number. Returns the security settings page, or the page\'s message if the change was not saved.',
	inputSchema: objectInput({
		signInMethod: stringInput('both lets the person choose at sign in; phone or email sends only there.', {enum: Object.keys(METHODS.signIn)}),
		phoneMethod: stringInput('Whether phone codes come by text or a phone call.', {enum: Object.keys(METHODS.phone)}),
	}),
	run: ({signInMethod, phoneMethod}) => {
		if (!signInMethod && !phoneMethod) throw new Error('Pass signInMethod, phoneMethod, or both.')
		if (signInMethod) chooseOption(METHODS.signIn[signInMethod], 'sign-in method choice')
		if (phoneMethod) chooseOption(METHODS.phone[phoneMethod], 'phone method choice')
		return submit('updatePhoneEmailSettingsForm', 'Done button')
	},
})

const buildListPasskeys = () => readOnlyTool({
	name: 'list_passkeys',
	description: 'List the passkeys saved on the person\'s account by name and when each was created, on the manage-passkeys page. Use a name from this list with delete_passkey, or add_passkey to make another. Nothing on the account changes, and no passkey ids are returned.',
	untrusted: true,
	execute: () => {
		const passkeys = readPasskeys().map(({name, created}) => ({name, created}))
		return {count: passkeys.length, passkeys}
	},
})

const buildAddPasskey = () => actionTool({
	name: 'add_passkey',
	description: 'Start creating a new passkey on this device, as "+ Add a passkey" does on the manage-passkeys page. The browser shows its own prompt, and only the person can approve it with their fingerprint, face, or device PIN. Tell them to approve it, then call get_auth_state.',
	startsPasskeyPrompt: true,
	run: () => pressButtonWithText('+ Add a passkey'),
})

const buildDeletePasskey = () => actionTool({
	name: 'delete_passkey',
	description: 'Delete one of the person\'s passkeys by the name list_passkeys shows, confirming in the site\'s own dialog. The person can no longer sign in with it and should also remove it from their device or password manager. It takes effect at once, so confirm with the person first.',
	inputSchema: objectInput({
		name: stringInput('The passkey name, exactly as list_passkeys shows it.', {maxLength: 120}),
		created: stringInput('When the passkey was created, as list_passkeys shows it. Needed only if two passkeys share a name.', {maxLength: 60}),
	}, ['name']),
	run: async ({name, created}) => {
		const matches = readPasskeys().filter((passkey) => passkey.name === name && (!created || passkey.created === created))
		if (!matches.length) throw new Error(`There is no passkey named "${name}". Call list_passkeys for the names on this account.`)
		if (matches.length > 1) throw new Error(`Several passkeys are named "${name}". Call again with created set to one of: ${matches.map((passkey) => passkey.created).join('; ')}.`)
		matches[0].tile.querySelector('button.delete-passkey').click()
		const confirm = await waitForElement('#deletePasskeyModal .modal-body button.btn:not(.btn-white)', 3000)
		if (!confirm || !isVisible(confirm)) throw new Error('The delete confirmation did not open. Call get_auth_state.')
		confirm.click()
	},
})

const buildLeavePage = (routeName) => () => navigationTool({
	name: 'leave_page',
	description: 'Leave this account page with one of its exit buttons, which returns to wherever the person came from: the account settings on this site, or the tax return. Unsaved changes on this page are dropped. Use navigate instead to open a different account page.',
	inputSchema: objectInput({button: stringInput('The exit button to press.', {enum: EXIT_BUTTONS[routeName]})}, ['button']),
	execute: ({button}) => {
		if (!EXIT_BUTTONS[routeName].includes(button)) throw new Error(`Use one of: ${EXIT_BUTTONS[routeName].join(', ')}.`)
		pressButtonWithText(button)
	},
})

const withLeavePage = (routeName, builders) => [...builders, buildLeavePage(routeName)]

export const accountTools = {
	AccountInfo: withLeavePage('AccountInfo', [buildAccountOverview, buildSendEmailVerification, buildSendPhoneVerification]),
	AccountActivity: withLeavePage('AccountActivity', [buildAccountActivity, buildShowMoreActivity, buildRemoveTrustedDevices]),
	EditUsername: withLeavePage('EditUsername', [buildChangeUsername]),
	EditPassword: withLeavePage('EditPassword', [buildChangePassword]),
	EditEmail: withLeavePage('EditEmail', [buildChangeEmail]),
	EditSecondEmail: withLeavePage('EditSecondEmail', [buildChangeSecondEmail, buildRemoveSecondEmail('editEmailForm')]),
	ConfirmDeleteSecondEmail: withLeavePage('ConfirmDeleteSecondEmail', [buildRemoveSecondEmail('deleteEmailForm')]),
	EditMobilePhone: withLeavePage('EditMobilePhone', [buildChangePhone]),
	ConfirmDeleteMobilePhone: withLeavePage('ConfirmDeleteMobilePhone', [buildRemovePhone]),
	VerifySMS: withLeavePage('VerifySMS', [buildVerifyPhone, buildResendPhoneCode, buildSkipPhoneVerification]),
	EditSecuritySettingsPassword: withLeavePage('EditSecuritySettingsPassword', [buildUnlockSecuritySettings]),
	EditSecuritySettings: withLeavePage('EditSecuritySettings', [buildSecuritySettings, buildOpenSecuritySetting, buildRemoveTrustedDevices]),
	EnableAuthApp: withLeavePage('EnableAuthApp', [buildSetAuthenticatorApp]),
	EnableAuthAppQrCode: withLeavePage('EnableAuthAppQrCode', [buildVerifyAuthenticatorApp]),
	EnableAuthAppSuccess: [buildSetAuthenticatorAppOnly, buildRegenerateEmergencyCodes, buildDownloadEmergencyCodes],
	DisableAuthAppSuccess: [buildKeepEmergencyCodes],
	EnableEmergencyCodes: withLeavePage('EnableEmergencyCodes', [buildSetEmergencyCodes, buildRegenerateEmergencyCodes, buildDownloadEmergencyCodes]),
	EmergencyCodes: [buildRegenerateEmergencyCodes, buildDownloadEmergencyCodes, buildFinish],
	DisableEmergencyCodesSuccess: [buildFinish],
	EnableTextVerification: withLeavePage('EnableTextVerification', [buildSignInVerificationSettings, buildSetSignInVerification]),
	Passkey: withLeavePage('Passkey', [buildListPasskeys, buildAddPasskey, buildDeletePasskey]),
}